// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — bootstrap and the screen machine. Owns which screen is up, the room's Net (created
// ONCE per session and never left for a rematch), and the setInterval that drives every clock.
// Rules live in rules.ts, the search in bot.ts, the round in duel.ts, the three-tap turn in
// turn.ts, and the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type RoundPlayer, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf } from './modes';
import { cellName, isOver, plyArrow, plyFrom, plyTo, territory, type PlyEvent } from './rules';
import { DEFAULT_TIER, optsForTier, searchTurn, tierOf } from './bot';
import { Duel, SEATS, seatFor, sideForRound, type WireMsg } from './duel';
import { BoardView, emptyHints, renderFinal, type LastMove, type ViewHints } from './render';
import { freshTurn, promptFor, stageOf, tap, targets, undo, type TurnUi } from './turn';
import { runCountdown } from './countdown';
import { analyse, type Analysis, type Verdict } from './analysis';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, verdictHtml } from './ui';
import { PALETTE, SEAT_COLOUR, SEAT_NAME } from './palette';
import { PATCHES } from './cues';

const SLUG = 'huntress';

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
// The custom patches MUST be passed here: `play()` on an unknown cue is a silent no-op, so a missing
// patch is an inaudible bug rather than an error. See cues.ts.
const sfx = createSfx({ muted: store.get<boolean>('muted', false) ?? false, patches: PATCHES });
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Hunter ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;

const recordKey = (id: string): string => `rec_${id}`;
function recordFor(id: string): [number, number] {
  const v = store.get<number[]>(recordKey(id), [0, 0]) ?? [0, 0];
  return [v[0] | 0, v[1] | 0];
}
function noteResult(id: string, won: boolean): void {
  const r = recordFor(id);
  store.set(recordKey(id), won ? [r[0] + 1, r[1]] : [r[0], r[1] + 1]);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let duel: Duel | null = null;
let roster: RoundPlayer[] = [];
let tally: [number, number] = [0, 0];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

/**
 * A sheet has FOUR ways out — the ✕, a tap outside, Escape, and a backdrop that is genuinely
 * reachable — because an overlay with one way out is a game you cannot play. The 350ms arming
 * delay is not a flourish: a sheet opened from a pointerup is otherwise closed instantly by that
 * same tap's trailing click.
 */
function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const armedAt = Date.now();
  const close = (): void => {
    wrap.remove();
    window.removeEventListener('keydown', onKey);
  };
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
  window.addEventListener('keydown', onKey);
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a turn */
  }
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, tierId: myTier, record: recordFor(myModeChoice), muted: sfx.muted() });

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = tierOf(c.dataset.tier).id;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let activeDuel: Duel | null = null;
let activeSubmit: ((ply: number) => boolean) | null = null;

function buildGameScreen(
  d: Duel,
  onQuit: () => void,
  onOver: () => void,
  onRestart: (() => void) | null,
): Screen {
  const mode = d.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-turn" id="hudTurn"></span>
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
      </div>
      <div class="clockbar" id="clockbar" hidden><i id="clockfill"></i></div>
      <div class="stages" id="stages"></div>
      <div class="board-host" id="boardHost"></div>
      <p class="prompt" id="prompt" aria-live="polite"></p>
      <div class="turn-actions" id="actions"></div>
      <aside class="side" id="side"><h3>Moves</h3><ul class="movelist" id="movelist"></ul><div class="land" id="land"></div></aside>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const hudTurn = content.querySelector<HTMLElement>('#hudTurn')!;
  const stagesEl = content.querySelector<HTMLElement>('#stages')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;
  const clockbar = content.querySelector<HTMLElement>('#clockbar')!;
  const clockfill = content.querySelector<HTMLElement>('#clockfill')!;
  const movelist = content.querySelector<HTMLElement>('#movelist')!;
  const landEl = content.querySelector<HTMLElement>('#land')!;

  const view = new BoardView(boardHost, d.state, (cell) => onCell(cell), reducedMotion);
  let ui: TurnUi = freshTurn();
  let live = false;
  let destroyed = false;
  let last: LastMove | null = null;
  let botTimer: ReturnType<typeof setTimeout> | null = null;
  let paused = false;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    paint();
    maybeBot();
  });

  const legalNow = (): number[] => (live ? d.legal() : []);

  function hints(): ViewHints {
    const h = emptyHints();
    h.last = last;
    h.mySide = d.mySide;
    h.interactive = live && d.isMyTurn;
    h.trapped = d.trappedCells();
    if (!live || !d.isMyTurn) return h;
    h.stage = stageOf(ui);
    h.picked = ui.from;
    h.pendingAt = ui.to;
    h.targets = targets(legalNow(), ui);
    return h;
  }

  function paint(): void {
    if (destroyed) return;
    const s = d.state;
    view.paint(s, hints());

    const side = s.turn;
    const yours = d.mySide === side;
    hudTurn.innerHTML =
      d.fault !== null
        ? '<span class="fault-note">Round stopped</span>'
        : isOver(s)
          ? `<span>${escapeHtml(s.winner >= 0 ? `${d.names[s.winner]} wins` : 'Round over')}</span>`
          : `<span class="rtok${side === 1 ? ' p2' : ''}"></span><span>${escapeHtml(
              d.mySide === null ? `${d.names[side]} to play` : yours ? 'Your turn' : `${d.names[side]} is thinking…`,
            )}</span>`;

    const st = stageOf(ui);
    stagesEl.innerHTML =
      isOver(s) || !live
        ? ''
        : `<b class="${st === 'idle' && yours ? 'on' : st !== 'idle' ? 'done' : ''}">① Run</b><span>→</span><b class="${st === 'moved' && yours ? 'on' : ''}">② Shoot</b>`;

    promptEl.textContent = promptText();
    renderActions();
    renderClock();
    renderMoves();
  }

  function promptText(): string {
    const s = d.state;
    if (d.fault === 'build') return 'Your opponent is on a different build of Huntress — reload to play again.';
    if (d.fault === 'desync') return 'The two boards stopped agreeing, so the round was stopped.';
    if (d.missing !== null) return `${d.names[d.missing]} dropped out — holding the round for ${Math.ceil(d.holdLeft / 1000)}s…`;
    if (isOver(s)) return '';
    if (!live) return '';
    if (d.mySide === null) return 'Watching — you are in the next round.';
    if (!d.isMyTurn) return `Waiting for ${d.names[s.turn]}…`;
    return promptFor(stageOf(ui), true);
  }

  function renderActions(): void {
    const canUndo = d.isMyTurn && stageOf(ui) !== 'idle';
    actions.innerHTML = `${canUndo ? '<button class="btn" id="undo">Undo</button>' : ''}<button class="btn ghost" id="quit">Quit</button>`;
    actions.querySelector('#undo')?.addEventListener('click', () => {
      if (undo(ui)) ping('blip', 0.7);
      paint();
    });
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
  }

  function renderClock(): void {
    if (d.clockMs <= 0 || isOver(d.state) || d.mySide === null) {
      clockbar.hidden = true;
      return;
    }
    clockbar.hidden = !d.isMyTurn;
    const frac = Math.max(0, Math.min(1, d.turnLeft / d.clockMs));
    clockfill.style.width = `${frac * 100}%`;
    clockbar.classList.toggle('low', frac < 0.25);
  }

  function renderMoves(): void {
    const s = d.state;
    const rows: string[] = [];
    s.log.forEach((ply, i) => {
      rows.push(
        `<li>${i + 1}. ${cellName(s, plyFrom(ply))}→${cellName(s, plyTo(ply))} ✕${cellName(s, plyArrow(ply))}</li>`,
      );
    });
    movelist.innerHTML = rows.slice(-26).join('');
    const t = territory(s);
    landEl.innerHTML = `<span>${escapeHtml(d.names[0])}: <b>${t.own[0]}</b></span><span>${escapeHtml(d.names[1])}: <b>${t.own[1]}</b></span>`;
  }

  // `last` is derived HERE, from the event stream, and nowhere else. Both the local submit and the
  // remote receive path emit events (duel.ts:159 and :216), so this is the one place that sees every
  // ply whoever played it. Setting it on the local submit path instead — which is what this build
  // shipped — leaves a P2P player looking at a trail marking their OWN previous move while the
  // opponent's move goes unmarked, and fires the arrow flight from the wrong square.
  function juice(events: PlyEvent[]): void {
    for (const e of events) {
      if (e.kind === 'move' && e.cell !== undefined && e.from !== undefined) {
        last = { from: e.from, to: e.cell, arrow: e.cell, side: e.seat ?? d.state.turn };
        ping('lift', 1);
      } else if (e.kind === 'burn' && e.cell !== undefined) {
        // The arrow is the signature moment, so it gets the flight, the thud and the shake.
        const from = last?.to ?? e.cell;
        if (last) last.arrow = e.cell;
        view.arrow(from, e.cell, PALETTE.ember);
        view.markFreshBurn(e.cell);
        view.burst(e.cell, PALETTE.ember, 12);
        ping('thud', 0.9);
        view.shake(2);
      } else if (e.kind === 'seal') {
        ping(e.seat === d.mySide ? 'win' : 'seal');
        view.shake(6);
      } else if (e.kind === 'territory') {
        ping(e.seat === d.mySide ? 'win' : 'lose');
      }
      if (!reducedMotion && 'vibrate' in navigator) {
        try {
          if (e.kind === 'seal') navigator.vibrate([12, 30, 12]);
          else if (e.kind === 'burn') navigator.vibrate(7);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
  }

  function submit(ply: number): boolean {
    // NB: `last` is set from the event stream in juice(), which d.submit() drives — see the note
    // there. Do not set it here as well, or the remote path silently diverges from the local one.
    const ok = d.submit(ply);
    if (!ok) return false;
    ui = freshTurn();
    paint();
    maybeBot();
    return true;
  }
  activeSubmit = submit;

  function onCell(cell: number): void {
    if (!live || destroyed || !d.isMyTurn) return;
    const res = tap(legalNow(), ui, cell);
    if (res.kind === 'commit') {
      submit(res.ply);
      return;
    }
    if (res.kind === 'reject') {
      view.nudge(cell);
      ping('thud', 0.6);
      return;
    }
    if (res.kind === 'advance') ping('select');
    else if (res.kind === 'cancel') ping('blip', 0.7);
    paint();
  }

  function maybeBot(): void {
    if (destroyed || paused || !live || !d.isBotTurn || isOver(d.state)) return;
    if (botTimer !== null) return;
    d.botThinking = true;
    // setTimeout, never rAF: a backgrounded tab throttles rAF to nothing and the opponent's turn
    // would simply never arrive.
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed || isOver(d.state)) return;
      const ply = searchTurn(d.state, optsForTier(tierOf(myTier), d.state.seed + d.state.ply)).ply;
      d.botThinking = false;
      if (ply >= 0) submit(ply);
    }, 340);
  }

  // Measure the REAL elapsed time rather than trusting the interval. A backgrounded tab clamps
  // timers to about once a second, so feeding a hard-coded 250ms makes every clock in the round run
  // at a quarter speed — a twenty-second disconnect hold takes eighty real seconds, which reads
  // exactly like it never fires at all.
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(5000, Math.max(0, now - lastTick));
    lastTick = now;
    // A SOLO pause really stops the clock. A multiplayer round's clock is shared, so a local pause
    // must NOT stop it — otherwise pausing would freeze the opponent's game too, and "pause" would
    // be a grief button. The pause card says which of the two you are getting.
    if (paused && d.clockMs <= 0) return;
    d.tick(dt);
  }, 250);

  // PAUSE ACTUALLY STOPS THE ROUND. The first cut of this only appended a card: the bot went on
  // thinking behind it and, in multiplayer, the turn clock kept draining and auto-played the move
  // the player had walked away from. A pause that does not pause is worse than no pause, because it
  // invites you to stop.
  //
  // In multiplayer the clock is shared and host-authoritative, so pausing it locally would be a way
  // to stop the other player's round: there, pause is offered as a read-only overlay that does NOT
  // halt the clock, and it says so. Solo is a true pause.
  function isPaused(): boolean {
    return !!boardHost.querySelector('.pause-ov');
  }

  function setPaused(on: boolean): void {
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      paused = true;
      if (botTimer !== null) {
        clearTimeout(botTimer);
        botTimer = null;
      }
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card" role="dialog" aria-modal="true" aria-label="Paused">
        <h2>Paused</h2>
        <p class="pause-note">${
          d.clockMs > 0
            ? 'The turn clock keeps running — your opponent is waiting.'
            : 'The hunt is held until you resume.'
        }</p>
        <button class="btn primary" id="resume">Resume</button>
        ${onRestart ? '<button class="btn" id="prestart">Restart</button>' : ''}
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      boardHost.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#prestart')?.addEventListener('click', () => {
        setPaused(false);
        onRestart?.();
      });
      ov.querySelector('#pquit')!.addEventListener('click', onQuit);
      // Route 2 of the dismissal contract: a tap anywhere off the card closes it. Armed on the next
      // frame so the tap that OPENED it cannot immediately close it again.
      setTimeout(() => {
        ov?.addEventListener('pointerdown', (ev) => {
          if (!(ev.target as HTMLElement).closest('.pause-card')) setPaused(false);
        });
      }, 350);
    } else {
      paused = false;
      ov?.remove();
      lastTick = Date.now();
      maybeBot();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!isPaused()));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!isPaused());
    else if (e.key === 'Escape') {
      // Escape closes the pause card if it is up — an overlay's Escape must close the overlay, not
      // quietly mutate the board behind it. Only when nothing is open does it undo a half-made ply.
      if (isPaused()) setPaused(false);
      else {
        undo(ui);
        paint();
      }
    } else if (e.key === 'u' || e.key === 'U') {
      undo(ui);
      paint();
    }
  }
  window.addEventListener('keydown', onKey);

  d.deps.onChange = paint;
  d.deps.onEvents = (events) => juice(events);
  d.deps.onEnd = () => {
    setTimeout(() => {
      if (!destroyed) onOver();
    }, 1000);
  };

  paint();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (botTimer !== null) clearTimeout(botTimer);
      cd.cancel();
      view.destroy();
      window.removeEventListener('keydown', onKey);
      if (activeSubmit === submit) activeSubmit = null;
    },
  };
}

// ── solo ────────────────────────────────────────────────────────────────────────
let soloScreen: Screen | null = null;

function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  // Alternate which side the player takes, so the first move alternates too.
  const mySide = soloRound % 2 === 1 ? 0 : 1;
  const names: [string, string] =
    mySide === 0 ? [playerName, `${tierOf(myTier).name}`] : [`${tierOf(myTier).name}`, playerName];
  const d = new Duel({
    mode,
    seed: newSeed(),
    round: soloRound,
    names,
    mySide,
    bot: optsForTier(tierOf(myTier)),
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  soloScreen = buildGameScreen(
    d,
    () => showMenu(),
    () => {
      if (d.state.winner >= 0) noteResult(mode.id, d.state.winner === mySide);
      showResults(false);
    },
    // Restart deals a fresh board of the same mode against the same tier. `soloRound` keeps
    // advancing, so the side you take still alternates rather than resetting to side 0.
    () => startSolo(),
  );
  onCleanup(() => soloScreen?.destroy());
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (m: WireMsg) => void = () => {};
let mvOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: Screen | null = null;
let beat: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    // Honoured ONCE, and cleared on the way in — otherwise a reload (or reopening from a home
    // screen icon) silently drags the player back into a room they deliberately left.
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Start a room and send the link, or type a code you were given.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [0, 0];
  duel = null;
  activeDuel = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    // ONCE at boot, before ANY mesh: Trystero pre-builds one global connection pool from the first
    // joinRoom's config, so a later call leaves the initiating half of every pair STUN-only.
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        // DELIBERATELY DOES NOT TOUCH THE DUEL. `onPeers` fires on any roster change — INCLUDING a
        // peer leaving — so calling peerBack() here immediately undid the peerLeft() that the very
        // same departure had just triggered: `missing` was cleared, `silence` was reset to zero, and
        // the walkover could never arm. The survivor sat on a live board waiting for someone who was
        // gone, forever. That is the exact failure the host-leave gate exists to catch, and it takes
        // a real two-tab run to see it.
        //
        // Presence is proven by a MESSAGE, not by a roster event: a peer that is genuinely back
        // sends its next heartbeat within 2s, and Duel.receive() clears `missing` and `silence` then.
        repaintLobby();
      },
      onPeerLeave: (id) => {
        // Same seat arithmetic as startNetRound, and the same reason for the slice: a SPECTATOR
        // leaving must not resolve to a seat, or the round holds — and eventually walks over — a
        // player who is still sitting there.
        const idx = roster.slice(0, SEATS).findIndex((p) => p.id === id);
        if (idx >= 0 && duel) duel.peerLeft(sideForRound(idx, roundNo));
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<WireMsg>('mv', (data) => duel?.receive(data));
  netSend = (m) => send(m);
  mvOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      // The host's pick is what the room plays, and it arrives GOSSIPED — rendering our own local
      // choice and labelling it the host's is a confident lie, and it has shipped once.
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  // HUNTRESS IS A DUEL: exactly the first TWO roster entries are players, and everyone else is a
  // spectator. `maxPlayers: 2` on the lobby is only a caption — the engine's rematch freezes
  // whatever roster voted, so a third peer really does arrive seated. Without this slice their
  // index runs through sideForRound() and aliases onto side 0 (`(i + round + 1) % 2` maps index 2
  // to the same side as index 0): two peers then submit different plies for the same side, both
  // latch `fault = 'desync'`, and the round dies for all three. Sharing an invite link into a group
  // chat is the ordinary way a room fills, so three people arriving is the expected case.
  const players = roster.slice(0, SEATS);
  const mySide = seatFor(players.map((p) => p.id), net?.selfId ?? '', info.round, info.seated);
  const names: [string, string] = ['', ''];
  players.forEach((p, i) => {
    names[sideForRound(i, info.round)] = p.name || SEAT_NAME[sideForRound(i, info.round)];
  });
  if (!names[0]) names[0] = SEAT_NAME[0];
  if (!names[1]) names[1] = SEAT_NAME[1];
  if (names[0] === names[1]) {
    // Two players CAN arrive with the same handle — the default one is random, and two tabs on one
    // device share the store outright. Identical names make a results screen unreadable.
    names[0] = `${names[0]} (${SEAT_NAME[0]})`;
    names[1] = `${names[1]} (${SEAT_NAME[1]})`;
  }

  const d = new Duel({
    mode,
    seed: info.seed,
    round: info.round,
    names,
    mySide,
    bot: null,
    clockMs: 60_000,
    deps: { send: (m) => netSend(m), onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s.
  beat = setInterval(() => d.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  gameScreen = buildGameScreen(
    d,
    () => {
      // End this peer's round FIRST (phase playing → waiting), or the engine lobby paints nothing
      // for a peer still seated in a live round and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    () => showResults(true),
    // No restart in a lockstep round: both peers replay one shared log, so one side re-dealing its
    // own board is a desync by construction. The rematch after the round is the way to play again.
    null,
  );
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
  d.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let analysis: Analysis | null = null;
let verdict: Verdict | null = null;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const d = duel;
  if (!d) {
    showMenu();
    return;
  }
  // THE TALLY IS PER PLAYER, NOT PER SEAT. Sides alternate every round (solo and multiplayer both),
  // so counting by `winner` — a SIDE index — and labelling it "Rust n — m Slate" reports which
  // COLOUR has won more, which is not a thing anyone wants to know and is not your record. Index 0
  // is always "you" from this peer's point of view; a spectator has no side, so it falls back to
  // seats and is labelled with them.
  if (d.state.winner >= 0 && d.state.reason !== 'nocontest') {
    tally[d.mySide === null ? d.state.winner : d.state.winner === d.mySide ? 0 : 1]++;
  }
  if (isMulti) rounds?.finish();

  content.innerHTML = `
    <section class="screen results-screen" id="res"></section>
    <div class="menu-actions" id="ract"></div>`;
  renderResults();

  const loser = d.state.winner >= 0 ? 1 - d.state.winner : -1;
  if (loser >= 0 && d.fault === null && d.state.reason !== 'walkover' && d.state.log.length > 6) {
    analysis = analyse(d.mode.id, d.state.seed, d.state.log, loser, { name: d.names[loser], isSelf: d.mySide === loser }, (v) => {
      verdict = v;
      const box = content.querySelector<HTMLElement>('#verdict');
      if (box) box.innerHTML = verdictHtml(v);
    });
  }
  onCleanup(() => {
    resultsMounted = false;
    analysis?.cancel();
    analysis = null;
    verdict = null;
  });
}

function renderResults(): void {
  const d = duel;
  if (!d || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  box.innerHTML = resultsHtml({
    rows: d.results(),
    mode: d.mode,
    reason: d.reason,
    winner: d.state.winner,
    tally,
    tallyLabels:
      d.mySide === null
        ? [d.names[0], d.names[1]]
        : ['You', d.names[1 - d.mySide]],
    round: multi ? roundNo : soloRound,
    fault: d.fault,
  });
  const canvas = box.querySelector<HTMLCanvasElement>('#finalBoard');
  if (canvas) renderFinal(canvas, d.state, territory(d.state).map, SEAT_COLOUR, PALETTE as unknown as Record<string, string>);
  const vbox = box.querySelector<HTMLElement>('#verdict');
  if (vbox) vbox.innerHTML = verdictHtml(verdict);

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const d = duel;
  if (!d) return;
  const me = d.mySide ?? 0;
  const won = d.state.winner === me;
  const rows = d.results();
  const text = `Huntress · ${d.mode.name} — ${won ? 'sealed them in' : 'ran out of ground'} holding ${rows[me].land} squares after ${rows[me].turns} turns\nhttps://ben-gy.github.io/huntress`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  duel = null;
  activeDuel = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  lobbyMounted = false;
  duel = null;
  activeDuel = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    mvOff();
  } catch {
    /* the channel may already be gone */
  }
  mvOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL submit path synchronously — rAF is
// throttled in a hidden tab, and every phone check runs through this. Never read by gameplay.
(window as unknown as { __huntress?: unknown }).__huntress = {
  state(): unknown {
    const s = activeDuel?.state;
    if (!s) return null;
    return {
      mode: s.mode.id,
      w: s.w,
      h: s.h,
      turn: s.turn,
      ply: s.ply,
      over: isOver(s),
      winner: s.winner,
      reason: s.reason,
      cell: Array.from(s.cell),
      pos: Array.from(s.pos),
      mySide: activeDuel?.mySide ?? null,
      log: s.log.slice(),
    };
  },
  legal(): number[] {
    return activeDuel?.legal() ?? [];
  },
  /** Disconnect bookkeeping, so a two-tab smoke test can SEE why a walkover did or did not arm. */
  diag(): unknown {
    const d = activeDuel;
    if (!d) return null;
    return { missing: d.missing, holdLeft: d.holdLeft, silence: d.silence, fault: d.fault, turnLeft: d.turnLeft };
  },
  play(ply: number): boolean {
    return activeSubmit ? activeSubmit(ply) : false;
  },
  /** Play one legal ply, chosen by the shipped search — enough to walk a whole game in a pane. */
  auto(): boolean {
    const d = activeDuel;
    if (!d || isOver(d.state)) return false;
    const ply = searchTurn(d.state, { depth: 1, timeMs: 0, nodes: 6000, kq: 6, ka: 2 }).ply;
    return ply >= 0 && !!activeSubmit && activeSubmit(ply);
  },
  colours: SEAT_COLOUR,
};

boot();
